// firebaseService — web shim that mimics the @react-native-firebase API surface
// (firebase.analytics().logEvent(...), firebase.remoteConfig.getValue(...).asString())
// so shared (non-.web) code can run unchanged on web.
import {initializeApp, getApps, getApp} from 'firebase/app';
import {
  getAnalytics,
  logEvent as fbLogEvent,
  setUserProperties as fbSetUserProperties,
  setUserId as fbSetUserId,
} from 'firebase/analytics';
import {getPerformance} from 'firebase/performance';
import {
  getRemoteConfig,
  getValue,
  fetchAndActivate,
  ensureInitialized,
  fetchConfig,
  activate,
} from 'firebase/remote-config';

import firebaseConfig from 'config/firebase.config.js';
import remoteConfigDefault from 'config/remoteConfigDefault.config.js';

let instance = null;

class FirebaseService {
  constructor() {
    console.log('[WEBGL_DBG] FirebaseService init starting');
    if (instance) return instance;

    const app =
      getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
    this.app = app;

    // Lazy analytics: returns RNFirebase-compatible facade so shared code's
    // firebaseService.analytics().logEvent(...) keeps working.
    let _analytics = null;
    const analyticsFacade = {
      logEvent: (name, params = {}) => {
        try { fbLogEvent(_analytics, name, params); } catch (e) {
          console.warn('[WEBGL_DBG] analytics.logEvent failed', e);
        }
      },
      logScreenView: ({screen_name, screen_class} = {}) => {
        try {
          fbLogEvent(_analytics, 'screen_view', {
            firebase_screen: screen_name,
            firebase_screen_class: screen_class,
          });
        } catch (e) { console.warn('[WEBGL_DBG] analytics.logScreenView failed', e); }
      },
      setCurrentScreen: (screen) => {
        try {
          fbLogEvent(_analytics, 'screen_view', {firebase_screen: screen});
        } catch (e) { console.warn('[WEBGL_DBG] analytics.setCurrentScreen failed', e); }
      },
      setUserProperties: (props) => {
        try { fbSetUserProperties(_analytics, props); } catch (e) {
          console.warn('[WEBGL_DBG] analytics.setUserProperties failed', e);
        }
      },
      setUserId: (id) => {
        try { fbSetUserId(_analytics, id); } catch (e) {
          console.warn('[WEBGL_DBG] analytics.setUserId failed', e);
        }
      },
    };
    this.analytics = () => {
      if (!_analytics) { try { _analytics = getAnalytics(app); } catch (e) { /* GA blocked → no-op */ } }
      return analyticsFacade;
    };

    this.performance = () => {
      try { return getPerformance(app); } catch (e) { return {}; }
    };

    // Remote Config: expose the RNFirebase-shaped methods on the same object
    // (callers do firebaseService.remoteConfig.getValue(key).asString()).
    const rc = getRemoteConfig(app);
    rc.defaultConfig = remoteConfigDefault;
    rc.settings = {...rc.settings, minimumFetchIntervalMillis: 60_000};

    const rcFacade = {
      getValue: (key) => getValue(rc, key),
      fetchAndActivate: () => fetchAndActivate(rc),
      fetch: () => fetchConfig(rc),
      activate: () => activate(rc),
      ensureInitialized: () => ensureInitialized(rc),
      setConfigSettings: (settings) => {
        rc.settings = {...rc.settings, ...settings};
      },
      setDefaults: (defaults) => {
        rc.defaultConfig = {...(rc.defaultConfig || {}), ...defaults};
      },
      // expose underlying instance for callers that pass it to top-level fns
      _instance: rc,
    };
    this.remoteConfig = rcFacade;

    console.log('[WEBGL_DBG] FirebaseService init complete; minimumFetchIntervalMillis=60000');
    instance = this;
    return instance;
  }
}

const firebaseService = new FirebaseService();
export default firebaseService;
